import { ChangeDetectionStrategy, Component, computed, inject, OnInit, signal } from "@angular/core";
import { AccountType, Marketplace } from "@front/m19-api-client";
import { AccountGroup } from "@front/m19-models";
import { IButtonDirective, MODAL_DATA, ModalRef } from "@front/m19-ui";
import { TranslocoDirective } from "@jsverse/transloco";
import { AccountGroupSelectorComponent } from "@m19-board/account-group-selector/account-group-selector.component";
import { MarketplaceSelectorComponent } from "@m19-board/marketplace-selector/marketplace-selector.component";
import { DashboardScope } from "@m19-board/services/dashboard-config.service";

@Component({
  selector: "app-settings-360-account-selection-modal",
  imports: [TranslocoDirective, IButtonDirective, AccountGroupSelectorComponent, MarketplaceSelectorComponent],
  templateUrl: "./settings-360-account-selection-modal.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Settings360AccountSelectionModalComponent implements OnInit {
  private readonly modalRef = inject(ModalRef);
  protected readonly data = inject<Settings360AccountSelectionModalData>(MODAL_DATA);

  selectedAccountGroup = signal<AccountGroup | undefined>(undefined);
  selectedMarketplace = signal<Marketplace | undefined>(undefined);
  selectedOrganizationId = signal<number | undefined>(undefined);
  accountGroupMarketplaces = computed<Marketplace[]>(
    () =>
      this.selectedAccountGroup()
        ?.getAccountMarketplaces()
        .map((am) => am.marketplace) ?? [],
  );

  ngOnInit() {
    this.selectedAccountGroup.set(this.data.dashboardAccountGroup);
    this.selectedMarketplace.set(this.data.dashboardAccountMarketplace?.marketplace);
    this.selectedOrganizationId.set(this.data.dashboardOrganizationId);
  }

  selectAccountGroup(accountGroup: AccountGroup | undefined) {
    if (!accountGroup?.id) accountGroup = undefined;
    this.selectedAccountGroup.set(accountGroup);
    this.selectedMarketplace.set(accountGroup?.getAccountMarketplaces()[0]?.marketplace);
  }

  selectMarketplace(marketplace: Marketplace | undefined) {
    if (!marketplace) return;
    this.selectedMarketplace.set(marketplace);
  }

  close() {
    this.modalRef.close();
  }

  updateDashboardAccount() {
    const dashboardAccountId = this.selectedAccountGroup()
      ?.getAccountMarketplaces()
      .find((am) => am.marketplace == this.selectedMarketplace())?.accountId;

    this.modalRef.close({
      accountId: dashboardAccountId,
      marketplace: this.selectedMarketplace(),
    });
  }
}

export interface Settings360AccountSelectionModalData {
  dashboardOrganizationId: number | undefined;
  dashboardAccountGroup: AccountGroup | undefined;
  dashboardAccountMarketplace: DashboardScope | undefined;
  dashboardAccountType: AccountType | undefined;
  isAuthorizedUserWriteAccess?: boolean;
}
